import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject, Input,
  OnDestroy, OnInit,
  Output,
  ViewChild } from '@angular/core';
import { untilDestroyed } from '@ngneat/until-destroy';
import { tap } from 'rxjs/operators';


import { ILocalizedToasterService, LOCALIZED_TOASTER_SERVICE_TOKEN } from '../../../../services/ILocalizedToasterService';
import { IQuestionEventsService, QUESTION_EVENTS_SERVICE_TOKEN } from '../../../../services/IQuestionEvents';
import { NumWithSignPipe } from '../../../pipes';

import { AddingblocksBlockType } from './addingblocks-block-type.enum';
import { AddingblocksRowsData } from './addingblocks-rows-data';

@Component({
  selector: 'kh-manip-addingblocks',
  templateUrl: './manip-addingblocks.component.html',
  styleUrls: ['./manip-addingblocks.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [NumWithSignPipe],
  standalone: false
})
export class ManipAddingblocksComponent implements OnInit, AfterViewInit, OnDestroy {
  public blockType!: AddingblocksBlockType;
  public blockValue!: number;
  public firstTerm!: number;
  public rowTiles!: number;
  public topValue = 0;
  public bottomValue = 0;
  public numZeroBlocks = 0;
  public total = 0;

  public topRow: AddingblocksRowsData[] = [];
  public bottomRow: AddingblocksRowsData[] = [];
  public hideStudentManipControlBar = this.questionEventsService.hideStudentManipControlBar.asObservable();
  public isManipChanged = false;
  @Input() public term1?: string;
  @Output() public valueChange = new EventEmitter<any>();
  @Output() public submitAnswer = new EventEmitter<string>();
  @ViewChild('add') public add!: ElementRef<HTMLElement>;
  @ViewChild('substract') public substract!: ElementRef<HTMLElement>;
  @ViewChild('sameSign') public sameSign!: ElementRef<HTMLElement>;
  @ViewChild('reset') public reset!: ElementRef<HTMLElement>;
  public questionClosed = false;
  public isClosedByTeacher = false;

  private val = '';
  private internallySet = false;

  public constructor(
    @Inject(LOCALIZED_TOASTER_SERVICE_TOKEN) private readonly toaster: ILocalizedToasterService,
    private readonly numWithSign: NumWithSignPipe,
    @Inject(QUESTION_EVENTS_SERVICE_TOKEN) private readonly questionEventsService: IQuestionEventsService,
    private readonly changeDetectorRef: ChangeDetectorRef) {
  }

  public get isOppositeSigns(): boolean {
    return this.blockType === AddingblocksBlockType.Opposite;
  }

  public get isSameSigns(): boolean {
    return this.blockType === AddingblocksBlockType.Same;
  }

  public get blockValueFormatted(): string {
    return this.numWithSign.transform(this.blockValue);
  }

  public get value(): string {
    return this.val;
  }

  @Input()
  public set value(value: string) {
    this.val = value;
  }

  public ngOnInit(): void {
    this.questionEventsService.hideAllInputForManip.next(true);
    this.questionEventsService.hideInputOnlyForManip.next(true);
    this.questionEventsService.controlClicked.next(false);

    if (!this.term1) {
      this.blockType = AddingblocksBlockType.Opposite;
    } else {
      this.firstTerm = parseInt(this.term1);

      this.blockType = AddingblocksBlockType.Same;
      this.blockValue = this.firstTerm > 0 ? 1 : -1;
    }

    if (!this.val) {
      this.Reset();
    }

    this.valueUpdated(this.val);
    this.questionEventsService.questionClosed
      .pipe(
        tap((eventInfo: { byTeacher?: boolean }) => {
          this.questionClosed = true;
          if (eventInfo?.byTeacher) {
            this.isClosedByTeacher = true;
          }
        }),
        untilDestroyed(this)
      )
      .subscribe();

    this.changeDetectorRef.markForCheck();
  }

  public submit(): void {
    this.submitAnswer.emit(this.value);
  }

  public ngAfterViewInit(): void {
    // Glow animation when the secondary buttons are not engaged for more than 5 seconds.
    // Animation stops when the student interacts with the question or clicks any control buttons.
    setTimeout(() => {
      if (!this.isManipChanged) {
        (this.add?.nativeElement)?.classList?.add('is-active');
        (this.substract?.nativeElement)?.classList?.add('is-active');
        (this.sameSign?.nativeElement)?.classList?.add('is-active');
        (this.reset?.nativeElement)?.classList?.add('is-active');
      }
    }, 5000);
  }

  public noZeroValue(value: number): boolean {
    return value !== 0;
  }

  public controlClicked(): void {
    this.isManipChanged = true;
    if (this.add?.nativeElement instanceof HTMLElement) {
      this.add.nativeElement.classList.remove('is-active');
    }
    if (this.substract?.nativeElement instanceof HTMLElement) {
      this.substract.nativeElement.classList.remove('is-active');
    }
    if (this.sameSign?.nativeElement instanceof HTMLElement) {
      this.sameSign.nativeElement.classList.remove('is-active');
    }
    if (this.reset?.nativeElement instanceof HTMLElement) {
      this.reset.nativeElement.classList.remove('is-active');
    }
    this.questionEventsService.controlClicked.next(true);
  }

  public AddBlock(row: AddingblocksRowsData[], addValue: number): void {
    this.controlClicked();
    if (row.length >= 8) {
      this.toaster.pop('info', 'Cannot add more blocks');
      return;
    }

    row.push({ value: addValue });
    this.updateResults();
  }

  public removeBlock(row: AddingblocksRowsData[], indexValue: number): void {
    this.controlClicked();
    row.splice(indexValue, 1);
    this.updateResults();
  }

  public resetClick(): void {
    this.isManipChanged = true;
    this.add?.nativeElement.classList.remove('is-active');
    this.substract?.nativeElement.classList.remove('is-active');
    this.sameSign?.nativeElement.classList.remove('is-active');
    this.reset?.nativeElement.classList.remove('is-active');
    this.Reset();
  }

  public Reset() {
    this.topRow = [];
    this.bottomRow = [];

    if (this.isSameSigns) {
      for (let i = 0; i < Math.abs(this.firstTerm); i++) {
        this.topRow.push({ value: this.blockValue });
      }
    }

    this.updateResults();
  }

  private updateResults(): void {
    this.rowTiles = Math.max(this.topRow.length, this.bottomRow.length);
    switch (this.blockType) {
      case AddingblocksBlockType.Opposite:
        this.topValue = this.topRow.length;
        this.bottomValue = -this.bottomRow.length;

        this.numZeroBlocks = Math.min(this.topValue, -this.bottomValue);

        break;
      case AddingblocksBlockType.Same:
        this.topValue = this.topRow.length * this.blockValue;
        this.bottomValue = this.bottomRow.length * this.blockValue;

        this.numZeroBlocks = 0;
        break;
    }

    this.total = this.topValue + this.bottomValue;
    this.internallySet = true;
    this.val = '(' + this.numWithSign.transform(this.topValue) + ') + ' +
    '(' + this.numWithSign.transform(this.bottomValue) + ') = ' + this.numWithSign.transform(this.total);

    this.valueChange.emit(this.value);
  }

  private valueUpdated(value: string): void {
    if (this.internallySet) {
      this.internallySet = false;
      return;
    }

    if (value.indexOf('=') <= 0) {
      return;
    }

    const expression: (number | string)[] = (value.split(' = ')[0]).split(' + ');
    expression[0] = parseInt((expression[0] as string).replace('(', '').replace(')', ''));
    expression[1] = parseInt((expression[1] as string).replace('(', '').replace(')', ''));

    this.topRow = [];
    this.bottomRow = [];

    switch (this.blockType) {
      case AddingblocksBlockType.Opposite:
        for (let i = 0; i < expression[0]; i++) {
          this.topRow.push({ value: 1 });
        }

        for (let i = 0; i < -expression[1]; i++) {
          this.bottomRow.push({ value: -1 });
        }

        break;
      case AddingblocksBlockType.Same:
        for (let i = 0; i < Math.abs(expression[0]); i++) {
          this.topRow.push({ value: this.blockValue });
        }

        for (let i = 0; i < Math.abs(expression[1]); i++) {
          this.bottomRow.push({ value: this.blockValue });
        }

        break;
    }

    this.updateResults();
  }

  public ngOnDestroy(): void {
    this.questionEventsService.controlClicked.next(false);
  }
}
